<template>
  <ElDialog
    width="100%"
    top="6vh"
    :visible="dialogIsVisible"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    class="guest-import-dialog"
    @close="onDialogClose"
  >
    <div slot="title">Import your guests</div>

    <div
      v-loading="ongoingParsing || isGeneratingGuestsToImport"
      element-loading-text="Checking your guest list.."
    >
      <Transition
        name="el-fade-in-linear"
        mode="out-in"
      >
        <GuestDropZone
          v-if="!importFileDataIsAvailable"
          show-border
        />
        <ImportForm
          v-else
          v-loading="isImportingGuests"
          element-loading-text="Importing guests.. One moment please"
          :status-options="guestStatusOptions"
          :show-barcode-value="event && event.isSupportingQRCode && event.guestIdFieldForCheckIn === 'barcodeValue'"
        />
      </Transition>
    </div>

    <div
      slot="footer"
      class="dialog-footer"
    >
      <div class="flex flex-col items-center justify-between laptop:flex-row-reverse">
        <div class="mr-20 mobile:mr-0 mobile:mb-20 mobile:w-full">
          <div
            v-if="guestImportLimitReached"
            class="flex flex-col items-center p-20 bg-gray-100 laptop:flex-row laptop:p-0 laptop:bg-transparent"
          >
            <FontAwesomeIcon
              :icon="['fas', 'exclamation-triangle']"
              class="text-2xl text-warning mb-10 laptop:mr-20 laptop:mb-0"
            />
            <span class="text-center">
              Guest limit of {{ event.maxGuests }} guests is reached
            </span>
          </div>
        </div>
        <div class="self-end flex justify-end laptop:flex-row-reverse">
          <ElButton
            type="text"
            :disabled="isImportingGuests"
            class="mr-20 cursor-pointer laptop:mr-0 laptop:mx-20"
            @click="onDialogClose"
          >
            Cancel
          </ElButton>
          <ElButton
            type="primary"
            :disabled="importButtonOptions.isDisabled"
            :loading="isGeneratingGuestsToImport"
            :title="importButtonOptions.title"
            class="action-button"
            @click="onGuestImportPress"
          >
            {{ importButtonOptions.label }}
          </ElButton>
        </div>
      </div>
    </div>
  </ElDialog>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import XLSX from 'xlsx';
import { chunk } from 'lodash-es';
import { FBFirestoreRef } from '@/helpers/firebaseServices';
import { FSGetEventGuests } from '@/helpers/firestoreRefs';
import { guestStatus, guestProperties } from '@/utils/utilityFunctions';
import { matchLocalizedColumn } from '@/utils/importColumnMatching';
import guestPluralRules from '@/utils/pluralRules/guest';
import { showErrorNotification, showSuccessNotification } from '@/utils/notification';
import GuestDropZone from '../GuestDropZone';
import ImportForm from './GuestImportForm';

const maxDeleteBatchSize = 500;
const maxImportBatchSize = 500;
const rowMatchNumberLimit = 10;
const generateColumns = (refstr) => Array(XLSX.utils.decode_range(refstr).e.c + 1)
  .fill(0)
  .map((x, i) => ({ name: XLSX.utils.encode_col(i), key: i }));

export default {
  props: {
    event: {
      type: Object,
      default: null,
    },
  },
  components: {
    GuestDropZone,
    ImportForm,
  },
  data: () => ({
    ongoingParsing: false,
    importDataIsValid: false,
    isDeletingAllGuests: false,
    parserOptions: {
      browserIsSupportingSW: false,
      swIsPending: true,
      fileReadType: {
        type: 'binary',
      },
      swFileRef: null,
    },
  }),
  created() {
    // const swRef = new Worker('service-worker.js');

    // this.parserOptions.browserIsSupportingSW = typeof Worker !== 'undefined';

    // swRef.onmessage = (e) => {
    //   switch (e.data.status) {
    //     case 'ready':
    //       console.error('# SW is ready');
    //       break;
    //     case 'e':
    //       this.parserOptions.swIsPending = false;
    //       console.log(e.data.parsedData);
    //       console.error(e.data.parsedData);
    //       break;
    //     case 'xlsx':
    //       this.parserOptions.swIsPending = false;
    //       console.log(e.data.parsedData);
    //       cb(JSON.parse(e.data.parsedData));
    //       break;
    //     default:
    //       console.log('# Default');
    //   }
    // };
  },
  watch: {
    selectedFiles: {
      immediate: true,
      handler(newValue) {
        if (newValue && newValue[0]) {
          this.ongoingParsing = true;
          this.$nextTick(() => {
            this.readFile(newValue[0]);
          });
        }
      },
    },
  },
  computed: {
    ...mapState('guest', {
      deletingGuestsTimerIsRunning: 'deletingGuestsTimerIsRunning',
      guests: 'guests',
      guestsCounts: 'guestsCounts',
    }),
    ...mapState('guest/import', {
      isImportingGuests: 'isImportingGuests',
      dialogIsVisible: 'dialogIsVisible',
      parsingIsOngoing: 'parsingIsOngoing',
      isGeneratingGuestsToImport: 'isGeneratingGuestsToImport',
      importDetails: 'importDetails',
      selectedFiles: 'selectedFiles',
      invalidRowCount: 'invalidRowCount',
      importFileData: 'importFileData',
      guestsToImport: 'guestsToImport',
    }),
    ...mapGetters('guest/import', {
      importFileDataIsAvailable: 'importFileDataIsAvailable',
      validRowCount: 'validRowCount',
      columnSelectionIsValid: 'columnSelectionIsValid',
    }),
    remainingGuestCount() {
      if (!this.event?.maxGuests) return 0;

      return this.event.maxGuests - (this.guestsCounts.mainGuest + this.guestsCounts.extraGuest);
    },
    importButtonOptions() {
      if (!this.columnSelectionIsValid) {
        return {
          label: 'Import guests',
          title: 'Match at least one column (first or last name)',
          isDisabled: true,
        };
      }

      let importIsAllowed = true;
      let guestImportCount = 0;
      guestImportCount = this.validRowCount < this.remainingGuestCount ? this.validRowCount : this.remainingGuestCount;

      if (this.importDetails.deleteExistingGuests) {
        guestImportCount = this.validRowCount < this.event.maxGuests ? this.validRowCount
          : this.event.maxGuests;
      }
      importIsAllowed = guestImportCount > 0;

      // console.log('guestImportCount', guestImportCount);
      // console.log('importIsAllowed', importIsAllowed);

      const isDisabled = !this.importFileDataIsAvailable
        || !this.validRowCount || !this.columnSelectionIsValid || this.isGeneratingGuestsToImport || !importIsAllowed;
      let label = 'Import guests';
      let title = 'Press to import guests';

      if (importIsAllowed) {
        label = `Import ${guestPluralRules(guestImportCount)}`;
        title = `Press to import ${guestPluralRules(guestImportCount)}`;
      }

      return {
        label,
        title,
        isDisabled,
      };
    },
    guestImportLimitReached() {
      return this.remainingGuestCount < this.validRowCount; // Only show that the limit was hit when a file was parsed
    },
    guestStatusOptions() {
      return Object.entries(guestStatus)
        // Filter statuses that should be visible
        .filter((status) => status[1].importOption && (status[1].isVisible
          && status[1]?.isVisible(this.event)))
        .sort((a, b) => a[1].order - b[1].order)
        .map((status) => ({ value: parseInt(status[0], 10), label: status[1].name }));
    },
  },
  methods: {
    ...mapActions('guest', {
      updateIsDeletingGuests: 'updateIsDeletingGuests',
    }),
    ...mapActions('guest/import', {
      resetGuestImportState: 'resetGuestImportState',
      updateDialogIsVisible: 'updateDialogIsVisible',
      updateIsImportingGuests: 'updateIsImportingGuests',
      updateImportFileData: 'updateImportFileData', // TODO: Remove
      updateSelectedColumns: 'updateSelectedColumns',
      updateInvalidRowCount: 'updateInvalidRowCount',
      generateGuestData: 'generateGuestData',
    }),
    async FBDeleteAllGuests() {
      this.updateIsDeletingGuests(true);

      let batchDeletedGuestsCount = 0;

      try {
        const chunkedGuests = chunk(this.guests, maxDeleteBatchSize);

        for (let i = 0; i < chunkedGuests.length; i += 1) {
          const deleteBatch = FBFirestoreRef.batch();

          chunkedGuests[i].forEach((guestData) => deleteBatch
            .delete(FSGetEventGuests(this.event.id).doc(guestData.id)));

          // eslint-disable-next-line no-await-in-loop
          await deleteBatch.commit().then(batchDeletedGuestsCount += chunkedGuests[i].length);
        }

        return Promise.resolve(batchDeletedGuestsCount);
      } catch (e) {
        console.error(e);

        return Promise.reject(e);
      } finally {
        this.updateIsDeletingGuests(false);
      }
    },
    async FBImportGuests() {
      this.updateIsImportingGuests(true);

      let batchImportMainGuestsCount = 0;
      let batchImportedExtraGuestsCount = 0;

      try {
        const chunkedGuests = chunk(this.guestsToImport, maxImportBatchSize);
        const maxGuestsToBeAdded = this.remainingGuestCount;

        for (let chunkIndex = 0; chunkIndex < chunkedGuests.length; chunkIndex += 1) {
          const writeBatch = FBFirestoreRef.batch();
          let stopImport = false;

          for (let guestIndex = 0; guestIndex < chunkedGuests[chunkIndex].length; guestIndex += 1) {
            batchImportedExtraGuestsCount += chunkedGuests[chunkIndex][guestIndex].totalExtraGuests || 0;

            if ((guestIndex + (chunkIndex * maxImportBatchSize) + batchImportedExtraGuestsCount)
              >= maxGuestsToBeAdded) {
              stopImport = true;

              break;
            }

            writeBatch.set(FSGetEventGuests(this.event.id).doc(), chunkedGuests[chunkIndex][guestIndex]);
          }

          // eslint-disable-next-line no-await-in-loop,no-loop-func
          await writeBatch.commit().then(() => { // TODO: Handle guest batch import in chunks and using SW
            // eslint-disable-next-line no-underscore-dangle
            batchImportMainGuestsCount += writeBatch._mutations.length;
          });

          if (stopImport) {
            break;
          }
        }

        // await dispatch('events/FBUpdateEventDocumentGuestCounts', { // Removed guest count updates
        //   mainGuest: batchImportMainGuestsCount,
        //   extraGuest: batchImportedExtraGuestsCount,
        // }, { root: true });

        return Promise.resolve(batchImportMainGuestsCount);
      } catch (e) {
        console.error(e);

        return Promise.reject(e);
      } finally {
        this.updateIsImportingGuests(false);
      }
    },
    onDialogClose() {
      this.updateDialogIsVisible(false);
      this.resetGuestImportState();
    },
    async onGuestImportPress() {
      if (this.importDetails.deleteExistingGuests) {
        await this.updateIsImportingGuests(true);

        this.isDeletingAllGuests = true;
        await this.FBDeleteAllGuests();
        this.importGuests();
      } else {
        this.importGuests();
      }
    },
    importGuests() {
      this.FBImportGuests().then((importedGuestCount) => {
        this.onDialogClose();
        showSuccessNotification(`You imported ${guestPluralRules(importedGuestCount)} successfully`, 4000);
      }).catch((error) => {
        showErrorNotification(error.message);
        console.error(error);
      });
    },
    parseWithSW(data, callback, readType) {
      const swRef = new Worker('service-worker.js');
      // console.log('# Sending message to SW');
      // console.log(data);
      // console.log(readType);

      swRef.postMessage({
        data,
        readType,
      });
    },
    async processWorkbook(workbook) {
      // console.log('# Workbook');
      // console.log(workbook);

      // Get first worksheet
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];

      // Convert Worksheet to array of arrays
      const fileData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      if (fileData.length === 0) return Promise.reject(Error('The File is empty, please select your guest list file.'));

      // Generate columns from file and update the selected columns to match the new column count
      const fileColumns = generateColumns(worksheet['!ref']); // Generating column data from file
      const selectedColumns = new Array(fileColumns.length).fill(''); // By default no column is selected
      const rowCount = fileData.length < rowMatchNumberLimit ? fileData.length : rowMatchNumberLimit;
      let invalidRowsIndexLimit = -1;

      // Matching existing columns that are detected
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        for (let colIndex = 0; colIndex < fileData[rowIndex].length; colIndex += 1) {
          if (fileData[rowIndex][colIndex]) {
            // A header in the customer's own language, matched whole rather than as a
            // substring, so "Cognome" cannot be read as the "nome" inside it.
            const localizedMatch = selectedColumns[colIndex]
              ? ''
              : matchLocalizedColumn(fileData[rowIndex][colIndex], selectedColumns);

            if (localizedMatch) {
              selectedColumns[colIndex] = localizedMatch;

              if (invalidRowsIndexLimit === -1) invalidRowsIndexLimit = rowIndex + 1;

              // eslint-disable-next-line no-continue
              continue;
            }

            // eslint-disable-next-line no-loop-func
            Object.entries(guestProperties).forEach(([guestProperty, value]) => {
              // Don't match barcodeValue if event option is not allowing it
              if (guestProperty !== 'barcodeValue'
                || (guestProperty === 'barcodeValue' && this.event?.isSupportingQRCode
                  && this.event?.guestIdFieldForCheckIn)) {
                if (value.isImportOption && !selectedColumns.includes(guestProperty)) {
                  for (let matchIndex = 0; matchIndex < value.importColumnMatching.length; matchIndex += 1) {
                    if (value.importColumnMatching[matchIndex] !== 'status'
                      && fileData[rowIndex][colIndex]
                        .toString()
                        .toLowerCase()
                        .includes(value.importColumnMatching[matchIndex])) {
                      // Handling auto detection of @ differently
                      if (value.importColumnMatching[matchIndex] === '@') {
                        invalidRowsIndexLimit = rowIndex;
                      } else if (invalidRowsIndexLimit === -1) {
                        invalidRowsIndexLimit = rowIndex + 1;
                      }
                      selectedColumns[colIndex] = guestProperty;

                      break;
                    }
                  }
                }
              }
            });
          }
        }
      }

      // Removing last rows that are not having any data
      let rowsToDeleteCount = 0;
      for (let rowIndex = fileData.length - 1; rowIndex >= 0; rowIndex -= 1) {
        if (fileData[rowIndex].length === 0) rowsToDeleteCount += 1;
        else break;
      }
      if (rowsToDeleteCount) fileData.splice(-1, rowsToDeleteCount);

      // Updating fileData to show the preview of the parsed data
      await this.updateImportFileData({
        fileData,
        fileColumns,
        invalidRowsIndexLimit,
      });

      // Updating automatched columns
      // console.log('# Sending selectedColumns');
      this.updateSelectedColumns(selectedColumns);
      this.generateGuestData();

      // this.ongoingParsing = false;
      return Promise.resolve();
    },
    async handleParsing(data) {
      // console.log('# Handling parsing');
      try {
        // Try to parse with SW
        if (this.parserOptions.browserIsSupportingSW) {
          // console.log('# Parsing with SW');
          this.parseWithSW(data, this.processWorkbook, this.parserOptions.fileReadType);
          return;
        }

        // Parse without SW
        // console.log('# Parsing without SW');
        const workbook = XLSX.read(data, this.parserOptions.fileReadType);
        await this.processWorkbook(workbook);
      } catch (error) {
        console.error(error);
        showErrorNotification(error.message, 4000);
      } finally {
        this.ongoingParsing = false;
      }
    },
    readFile(file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        // console.log('# Reading file');
        if (e.target.result.length > 1e6) {
          // console.log('# File is over 1MB (parsing could take a while)');
        } else {
          // console.log('# Parsing small file');
        }
        this.handleParsing(e.target.result);
      };

      // Start file reader
      reader.readAsBinaryString(file);
    },
  },
};
</script>

<style scoped>
.guest-import-dialog >>> .el-dialog {
  @apply max-w-1000;
}
</style>
